import { useState } from 'react';
import { RiDownloadLine, RiExternalLinkLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import type { JobDocument } from '@/types/worker';
import styles from './DocumentPreview.module.css';

interface DocumentPreviewProps {
    document: JobDocument;
}

function getMediaType(mimeType: string): 'image' | 'video' | 'audio' | 'pdf' | 'other' {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'other';
}

function formatFileSize(bytes: number | null): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentPreview({ document: doc }: DocumentPreviewProps) {
    const [expanded, setExpanded] = useState(true);
    const mediaType = getMediaType(doc.mime_type);
    const hasPreview = mediaType !== 'other';

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.info}>
                    <span className={styles.name}>{doc.name}</span>
                    {doc.file_size && (
                        <span className={styles.size}>{formatFileSize(doc.file_size)}</span>
                    )}
                    {doc.is_secret && <span className={styles.secret}>Confidencial</span>}
                </div>
                <div className={styles.actions}>
                    {hasPreview && (
                        <button
                            className={styles.actionBtn}
                            onClick={() => setExpanded(!expanded)}
                            aria-label={expanded ? 'Ocultar vista previa' : 'Mostrar vista previa'}
                        >
                            {expanded ? <RiEyeOffLine size={16} /> : <RiEyeLine size={16} />}
                        </button>
                    )}
                    <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.actionBtn}
                        aria-label="Abrir en nueva pestaña"
                    >
                        <RiExternalLinkLine size={16} />
                    </a>
                    <a
                        href={doc.file_url}
                        download={doc.name}
                        className={styles.actionBtn}
                        aria-label="Descargar"
                    >
                        <RiDownloadLine size={16} />
                    </a>
                </div>
            </div>

            {hasPreview && expanded && (
                <div className={styles.preview}>
                    {mediaType === 'image' && (
                        <img
                            src={doc.file_url}
                            alt={doc.name}
                            className={styles.image}
                            loading="lazy"
                        />
                    )}

                    {mediaType === 'video' && (
                        <video
                            src={doc.file_url}
                            controls
                            className={styles.video}
                            preload="metadata"
                        >
                            Tu navegador no soporta la reproduccion de video.
                        </video>
                    )}

                    {mediaType === 'audio' && (
                        <audio
                            src={doc.file_url}
                            controls
                            className={styles.audio}
                            preload="metadata"
                        >
                            Tu navegador no soporta la reproduccion de audio.
                        </audio>
                    )}

                    {mediaType === 'pdf' && (
                        <iframe
                            src={doc.file_url}
                            title={doc.name}
                            className={styles.pdf}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
